import React, { useState, useContext, forwardRef } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { ThemeContext } from "../Theme";

import { mapChildrenIndexedByType } from "../../helper/map-children";
import { useUniqueId } from "../hooks/useUniqueId";
import A11yText from "../A11yText";
import { ButtonGroupProvider } from "./ButtonGroupContext";
import ButtonGroupItem from "./ButtonGroupItem";
import ButtonGroupIcon from "./ButtonGroupIcon";

import styles from "./ButtonGroup.scss";

export const ButtonGroup = forwardRef((props, ref) => {
  const {
    onChange,
    children,
    labelVisible,
    label,
    scheme,
    ...restProps
  } = props;
  const [value, setValue] = useState("");
  const context = useContext(ThemeContext);
  const { name } = restProps;
  const buttonGroupName =
    name || `button-group-${useUniqueId("ButtonGroup", props) + 1}`;

  function onCheck(val, event) {
    setValue(val);
    if (onChange) onChange(val, event);
  }

  const classes = cx(scheme, {
    [styles.buttonGroup]: !scheme,
    [styles[context]]: context,
  });

  return (
    <fieldset className={classes} ref={ref} {...restProps}>
      {labelVisible ? (
        <>
          <legend className={styles.legend}>{label}</legend>
        </>
      ) : (
        <A11yText tag="legend">{label}</A11yText>
      )}

      <div className={styles.inner}>
        <ul className={styles.items}>
          {mapChildrenIndexedByType(children, (child, childIndex) => (
            <ButtonGroupProvider
              value={{
                value,
                onCheck,
                name: buttonGroupName,
                childId: `${buttonGroupName}-radio-${childIndex + 1}`,
              }}
            >
              {child}
            </ButtonGroupProvider>
          ))}
        </ul>
      </div>
    </fieldset>
  );
});

ButtonGroup.propTypes = {
  label: PropTypes.string.isRequired,
  labelVisible: PropTypes.bool,
  onChange: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  scheme: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
};

ButtonGroup.defaultProps = {
  onChange: null,
  labelVisible: false,
  scheme: "",
};

ButtonGroup.Variant = {
  themeDark: "asterisk--theme_dark",
};

ButtonGroup.Item = ButtonGroupItem;

ButtonGroup.Icon = ButtonGroupIcon;

ButtonGroup.displayName = "ButtonGroup";

export default ButtonGroup;
